import { BetVm } from './BetVm';
import { StraightComparisonVm } from './StraightComparisonVm';
import { ParlayComparisonVm } from './ParlayComparisonVm';

import { translation } from '../locales/en/translation'; 
import { getTotalToWin } from '../helper/calculator';
import { currencyFormatter } from '../helper/currencyFormatter';

export class BetSlipVm {
    bets: BetVm[] = [];
    straightComparisonVm: StraightComparisonVm;
    parlayComparisonVm: ParlayComparisonVm;
    parlayWager: string = currencyFormatter.format(10.00);

    constructor() {
        this.straightComparisonVm = new StraightComparisonVm(this.bets);
        this.parlayComparisonVm = new ParlayComparisonVm(this.bets, this.parlayWager);
    }

    // Method to add a BetVm to the bets list
    addBet(bet: BetVm) {
        this.bets.push(bet);
        this.updateStraightComparisonVm();
        this.updateParlayComparisonVm();
    }

    // Method to remove a BetVm from the bets list
    removeBet(index: number) {
        this.bets.splice(index, 1);
        this.updateStraightComparisonVm();
        this.updateParlayComparisonVm();
    }

    private updateStraightComparisonVm(): void {
        this.straightComparisonVm = new StraightComparisonVm(this.bets);
    }

    private updateParlayComparisonVm(): void {
        this.parlayComparisonVm = new ParlayComparisonVm(this.bets, this.parlayWager);
    }

    // Method to remove a BetVm from the bets list
    removeMultipleBets(indexList: number[]) {
        if (indexList) {
            indexList.reverse().map(index => {
                this.bets.splice(index, 1);
            })
            this.updateStraightComparisonVm();
            this.updateParlayComparisonVm();
        }
    }

    // Method to retrieve all BetVm instances from the bets list
    getBets(): BetVm[] {
        return this.bets;
    }

    // Method to retrieve the url for the game APIs and build the cookie data object
    getCookieBets(): any {
        // I will need the api url, the wager for straight and parlay and the selected list.
        const cookieBets: any = {};
        cookieBets.bets = [];
        cookieBets.parlayWager = this.parlayWager;

        this.bets.map((val) => {
            const obj: any = {
                dataAttributes: {
                    apiUrl: val.apiUrl,
                    selectedSide: val.selectedSide,
                    selectedSportsbook: val.selectedSportsbook,
                },
                straightWager: val.straightWager,
                isActiveLeg: val.isActiveLeg   
            }
            cookieBets.bets.push(obj);
        })
        return cookieBets;
    }

    // Get total number of bets in the slip
    getTotalBets() {
        return this.bets.length;
    }    

    getTotalWins() {
        let totalWin = 0;
        this.bets.map((bt: any, index: number) => {
            totalWin += getTotalToWin(bt.selectedBet?.oddsDecimal, this.bets[index].straightWager)
        })
        return totalWin;
    }

    // Method to identify games that have started or finished
    getStartedGames(index?: number) {
        const now = new Date();

        const selectedBet = index ? 
                this.bets[index].game.startDate.getTime() < now.getTime() ? 
                    this.bets
                : 
                    [] 
            : this.bets.filter(slt => slt?.game?.startDate?.getTime() < now.getTime())

        return selectedBet.length > 0 ? translation.warning_messages.match_in_progress : '';
    }

    // Method to identify games that have started or finished
    getOutadedGamesIndex() {
        const now = new Date();
        const startedGames: any = [];

        this.bets.map((slt: any, index: number) => {
            if (slt.game?.startDate?.getTime() < now.getTime()) {

                startedGames.push(index);
            }
        })
            
        return startedGames;
    }

    // Method to prevent bets from the same game being added to the slip for parlays
    isSameGame(actBets: number[]) {
        const sameGameId: string[] = [];
        this.bets.map((bet1, index: number) => {
            if (actBets[index] !== -1) { 
                this.bets.map((bet2, index2: number) => {
                    if (actBets[index2] !== -1) { 
                        if (bet1.game.gameId === bet2.game.gameId && index !== index2) {
                            const found = sameGameId.find((ids: string) => ids === bet1.game.gameId);
                            if (!found) sameGameId.push(bet1.game.gameId);
                        }
                    }
                }) 
            }   
        })
        return sameGameId;
    }

    // Method to identify if selected games has different partners in the list of active bets
    hasDifferentPartners(actBets: number[]) {
        let diffPartners = false;
        let sameGames = false;
        this.bets.map((bet1, index: number) => {
            if (actBets[index] !== -1) {
                this.bets.map((bet2, index2: number) => {
                    if (actBets[index2] !== -1) {
                        if (bet1.game.gameId === bet2.game.gameId && index !== index2) {
                            sameGames = true;
                        }
                        if (bet1.selectedBet?.sportsbook?.sportsbookFeedKey !== bet2.selectedBet?.sportsbook?.sportsbookFeedKey) {
                            diffPartners = true;
                            return;
                        }
                    }
                })  
            }  
        })
        
        if (sameGames) {
            return false; 
        }
        return diffPartners;
    }

    // Method to prevent duplicate bets from being added to the slip
    isDuplicateBet(bet: BetVm) {
        const selectedBet = this.bets.filter(slt => slt.betKey === bet.betKey)

        return selectedBet.length > 0 ? true : false;
    }

    // Method to update the wager for a specific game (this is use in the straight tab only)
    updateStraightWager(index: number, q: string) {
        this.bets[index].straightWager = q
        this.updateStraightComparisonVm();
    }

    // Method to update the parlay wager
    updateParlayWager(q: string) {
        this.parlayWager = q
        this.updateParlayComparisonVm();
    }

    // Method to update the game selection (used in the parlay tab -> checkbox)
    updateSelectedLeg(index: number) {
        this.bets[index].isActiveLeg = !this.bets[index].isActiveLeg
        this.updateParlayComparisonVm();
    }

    // Method to populate the state value that helps the rerendering when updating the input field
    getStraightWagers() {
        const tmp: any = []
        this.bets.map((item) => {
            tmp.push(item.straightWager)
        });
        return tmp
    }
}
